import { usePatchTvvBusEta, usePostTvvBusEta } from "@/entities/tvv";
import { TvvBus } from "@/entities/tvv/domain/tvv-bestelde-route.ts";
import {
    Button,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Form,
    FormFieldInput,
} from "@/shared/components/ui";
import { hourFormatInAmsterdam, Time, timeToDate } from "@/shared/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import z from "zod";

const addEtaFormSchema = z.object({
    eta: z.iso.time({ precision: -1 }),
});

type EtaDialogProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    selectedBus: TvvBus | undefined;
    stationCode: string;
    stationNaam: string;
    routeId: number;
    dossierId: string;
};

type AddEtaFormData = z.infer<typeof addEtaFormSchema>;

export function EtaDialog({ open, setOpen, selectedBus, stationCode, stationNaam, routeId, dossierId }: Readonly<EtaDialogProps>) {
    const isEditing = selectedBus !== undefined;
    const currentEta = selectedBus !== undefined ? selectedBus.tijd : undefined;
    const existingEtaTime = currentEta ? hourFormatInAmsterdam(currentEta) : undefined;

    const form = useForm<AddEtaFormData>({
        resolver: zodResolver(addEtaFormSchema),
        defaultValues: {
            eta: existingEtaTime,
        },
    });

    const postEta = usePostTvvBusEta();
    const patchEta = usePatchTvvBusEta();

    const isPending = postEta.isPending || patchEta.isPending;

    const onOpenChange = (open: boolean) => {
        setOpen(open);
        if (!open) {
            form.reset();
        }
    };

    const onCancel = () => {
        setOpen(false);
        form.reset();
    };

    const handleSubmit = (data: AddEtaFormData) => {
        const tijd = timeToDate(data.eta as Time).toISOString();

        const newEta = { stationcode: stationCode, tijd };
        const mutateOptions = { onSuccess: () => onOpenChange(false) };

        if (isEditing && selectedBus !== undefined) {
            patchEta.mutate({ dossierId, routeId, busId: selectedBus.id, eta: tijd }, mutateOptions);
        } else {
            postEta.mutate({ dossierId, routeId, eta: newEta }, mutateOptions);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(open) => !isPending && onOpenChange(open)}>
            <DialogContainer>
                <DialogHeader>
                    <DialogTitle>{isEditing ? `ETA wijzigen ${stationNaam}` : `ETA toevoegen ${stationNaam}`}</DialogTitle>
                    <DialogDescription className="sr-only">
                        {isEditing ? `ETA wijzigen ${stationNaam}` : `ETA toevoegen ${stationNaam}`}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={(e) => void form.handleSubmit(handleSubmit)(e)}>
                        <DialogBody>
                            <FormFieldInput form={form} name="eta" label="ETA" type="time" className="max-w-1/3" />
                        </DialogBody>
                        <DialogFooter className="flex flex-row justify-end gap-2">
                            <Button type="button" variant="tertiary" onClick={onCancel} disabled={isPending}>
                                Annuleren
                            </Button>
                            <Button type="submit" variant="primary" disabled={isPending || !form.formState.isValid}>
                                {isEditing ? "Opslaan" : "Toevoegen"}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContainer>
        </Dialog>
    );
}
